import React, { useState, useEffect, useCallback } from 'react';
import { FormFieldSelect } from '../../../../components/common/select';
import { useParams, useNavigate } from 'react-router-dom';
import { formatCurrency as formatCurrencyUtil } from '../../../../utils/formatters';
import numberToWords from '../../../../utils/numberToWords';
import InvoiceForm from '../InvoiceForm';
import ItemForm from './ItemForm';
import PaymentForm from '../../../Payment/components/PaymentForm';
import Alert from '../../../../components/Alert/Alert';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';
import api from '../../../../api';
import { getEmailSettings, getInvoiceEmailTemplates, sendInvoiceEmail, getInvoiceClientEmail, markInvoiceSentToClient } from '../../api/invoiceEmailApi';
import { ArrowLeft } from 'lucide-react';
import ProposalItemsTable from './ProposalItemsTable';
import { flattenProposalBreakdown } from './flattenProposalBreakdown';
import { useAuth } from '../../../../contexts/AuthProvider';
import { validateInvoiceForm } from '../../../../utils/validation';

// ✅ MULTI-CURRENCY: Custom formatCurrency function for InvoiceView
const formatCurrency = (amount, currencyCode = 'IDR') => {
    // Use appropriate locale based on currency for proper formatting
    // USD uses en-US locale to show $ symbol, IDR uses id-ID locale
    const locale = currencyCode === 'USD' ? 'en-US' : 
                   currencyCode === 'EUR' ? 'de-DE' :
                   currencyCode === 'GBP' ? 'en-GB' :
                   currencyCode === 'JPY' ? 'ja-JP' :
                   currencyCode === 'SGD' ? 'en-SG' :
                   currencyCode === 'AUD' ? 'en-AU' :
                   'id-ID'; // Default to IDR locale
    
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0
    }).format(amount);
};

const InvoiceView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    // Email states
    const [emailSettings, setEmailSettings] = useState(null);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [isItemModalOpen, setIsItemModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [alert, setAlert] = useState(null);
    const [showSendConfirmation, setShowSendConfirmation] = useState(false);
    const [showDeleteItemModal, setShowDeleteItemModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [showFinalizeModal, setShowFinalizeModal] = useState(false);
    
    // ✅ FORM STATE: Add formData and formErrors to match Invoice/index.jsx behavior
    const [formData, setFormData] = useState({
        client: '',
        projectName: '',
        projectId: null,
        leadId: null,
        billDate: new Date().toISOString().split('T')[0],
        dueDate: '',
        description: '',
        jurnalNumber: '',
        invoiceTo: '',
        invoiceIssued: '',
        tax: 0,
        applyTax: false,
        documents: [],
        subtotal: 0,
        dppLainnya: 0,
        amountReceived: 0,
        message: '',
        notes: '',
        terms: '',
        paymentMethod: '',
        bankAccount: ''
    });
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Payment modal states
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [paymentFormData, setPaymentFormData] = useState({
        client: '',
        invoice: '',
        paymentMethod: '',
        paymentDate: new Date().toISOString().split('T')[0],
        amount: '',
        note: '',
        file: null
    });
    const [paymentFormErrors, setPaymentFormErrors] = useState({});
    
    // Use global auth context with capabilities
    const { can, hasRole } = useAuth();
    const isFinanceOrAdmin = hasRole('ROLE_ADMIN') || hasRole('ROLE_FINANCE') || hasRole('ROLE_DIRECTOR');
    
    // Helper function to check invoice-specific capabilities
    const canInvoice = (capabilityKey) => can(capabilityKey, 'invoice');

    // ✅ MULTI-CURRENCY: Add currency state and helper functions
    const [invoiceCurrencyCode, setInvoiceCurrencyCode] = useState('IDR');

    // ✅ FILE UPLOAD: Add state for uploaded file
    const [uploadedFile, setUploadedFile] = useState(null);

    // ✅ PROPOSAL ITEMS TABLE: State for proposal breakdown and import
    const [proposalBreakdown, setProposalBreakdown] = useState(null);
    const [proposalRows, setProposalRows] = useState([]);
    const [isLoadingBreakdown, setIsLoadingBreakdown] = useState(false);
    const [hasImportedProposal, setHasImportedProposal] = useState(false);
    const [isImportingProposal, setIsImportingProposal] = useState(false);

    // ✅ COA: Chart of Account integration (Finance/Admin only)
    const [coaOptions, setCoaOptions] = useState([]);
    const [savingCoaItemId, setSavingCoaItemId] = useState(null);


    // ✅ MULTI-CURRENCY: Helper function untuk mendapatkan currency symbol
    const getCurrencySymbol = (currencyCode) => {
        switch (currencyCode) {
            case 'USD': return '$';
            case 'EUR': return '€';
            case 'JPY': return '¥';
            case 'GBP': return '£';
            case 'SGD': return 'S$';
            case 'AUD': return 'A$';
            case 'IDR': return 'Rp';
            default: return 'Rp';
        }
    };

    // ✅ MULTI-CURRENCY: Helper function untuk mendapatkan currency prefix
    const getCurrencyPrefix = (currencyCode) => {
        const prefix = getCurrencySymbol(currencyCode) + ' ';
        return prefix;
    };

    const handleBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate('/invoice');
        }
    };

    const showAlert = useCallback((type, message) => {
        setAlert({ type, message });
        setTimeout(() => setAlert(null), 5000); // Auto hide after 5 seconds
    }, []);

    useEffect(() => {
        const initializeData = async () => {
            await fetchInvoiceDetail();
        };
        initializeData();
    }, [id]);

    useEffect(() => {
        if (isFinanceOrAdmin) {
            api.get('/crm/chart-of-accounts').then(res => {
                setCoaOptions(res.data || []);
            }).catch(() => {});
        }
    }, [isFinanceOrAdmin]);

    const fetchInvoiceDetail = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await api.get(`/crm/invoices/${id}`);

            setInvoice(response.data);

            // ✅ MULTI-CURRENCY: Use invoice currency if available, otherwise keep IDR fallback
            const invoiceCurrency = response.data.displayCurrencyCode || 
                                   response.data.currencyCode || 
                                   response.data.invoiceCurrencyCode;
            
            if (invoiceCurrency) {
                setInvoiceCurrencyCode(invoiceCurrency);
            }
        } catch (error) {
            console.error('Error fetching invoice detail:', error);
            setError('Failed to load invoice details');
        } finally {
            setLoading(false);
        }
    };

    // ✅ PROPOSAL ITEMS TABLE: Fetch breakdown when project-based invoice loads
    useEffect(() => {
        if (!invoice?.projectId || !invoice?.leadId) return;
        setIsLoadingBreakdown(true);
        Promise.all([
            api.get(`/crm/proposals/${invoice.leadId}/breakdown`),
            api.get('/crm/currency-rates').catch(() => ({ data: [] })),
            api.get(`/crm/proposals/detail/lead/${invoice.leadId}`).catch(() => ({ data: null }))
        ])
            .then(([res, ratesRes, proposalRes]) => {
                if (res.data?.years && res.data?.breakdown) {
                    const data = res.data;
                    const rates = Array.isArray(ratesRes.data) ? ratesRes.data : [];
                    const createdAt = proposalRes.data?.dates?.created_at || proposalRes.data?.createdAt || proposalRes.data?.created_at;
                    const refDate = new Date(createdAt || new Date());
                    refDate.setHours(0, 0, 0, 0);
                    const resolveRate = (code) => {
                        if (!code || code.toUpperCase() === 'IDR') return 1;
                        const matching = rates.filter(rate =>
                            (rate.currency_code || rate.currencyCode || '').toUpperCase() === code.toUpperCase()
                            && (rate.startdate || rate.start_date || rate.startDate)
                        );
                        const exact = matching.find(rate => {
                            const start = new Date(rate.startdate || rate.start_date || rate.startDate);
                            start.setHours(0, 0, 0, 0);
                            const endRaw = rate.enddate || rate.end_date || rate.endDate;
                            const end = endRaw ? new Date(endRaw) : null;
                            if (end) end.setHours(23, 59, 59, 999);
                            return refDate >= start && (!end || refDate <= end);
                        });
                        if (exact) return Number(exact.exchange_rate || exact.exchangeRate || 1);
                        const latest = matching.sort((a, b) => new Date(b.startdate || b.start_date || b.startDate) - new Date(a.startdate || a.start_date || a.startDate))[0];
                        return latest ? Number(latest.exchange_rate || latest.exchangeRate || 1) : 1;
                    };
                    const invoiceRate = Number(invoice.exchangeRateAtCreation || 1) || 1;
                    data.breakdown.forEach(service => service.standards?.forEach(standard =>
                        standard.serviceItems?.forEach(item => {
                            const code = item.currencyCode || 'IDR';
                            const raw = Number(item.unitPrice || 0) * Number(item.qty ?? 1);
                            // Import rows must remain in the proposal currency. Do not
                            // replace non-IDR proposal values with their IDR equivalent;
                            // the invoice detail/list performs conversion only for display.
                            item.prices = (item.years || []).map(() => raw);
                            item.exchangeRateAtCreation = code.toUpperCase() === 'IDR' ? 1 : invoiceRate;
                        })
                    ));
                    setProposalBreakdown(data);
                    setProposalRows(flattenProposalBreakdown(data));
                    setHasImportedProposal(false);
                }
            })
            .catch((err) => {
                console.error('Failed to load proposal breakdown:', err);
                setProposalBreakdown(null);
            })
            .finally(() => setIsLoadingBreakdown(false));
    }, [invoice?.projectId, invoice?.leadId]);

    // ✅ PROPOSAL ITEMS TABLE: Import checked proposal rows as invoice items
    const handleImportProposalItems = async (rows) => {
        if (!invoice?.id) return;
        const checked = rows.filter((r) => r.checked);
        if (checked.length === 0) return;
        setIsImportingProposal(true);
        try {
            await Promise.all(
                checked.map((row) =>
                    api.post('/crm/invoice-items', {
                        invoiceId: invoice.id,
                        description: row.description,
                        quantity: row.qty,
                        unitPrice: row.unitPrice,
                        discount: 0,
                        isTaxed: false,
                        // Keep the proposal's native amount. Conversion is display-only.
                        currencyCode: row.currencyCode || 'IDR',
                        baseCurrencyCode: 'IDR',
                        // Use the invoice's frozen creation rate, not the
                        // proposal rate or the current exchange rate.
                        exchangeRateAtCreation: (row.currencyCode || 'IDR').toUpperCase() === 'IDR'
                            ? 1
                            : Number(invoice.exchangeRateAtCreation || row.exchangeRateAtCreation || 1),
                    })
                )
            );
            await fetchInvoiceDetail();
            setHasImportedProposal(true);
            showAlert('success', `${checked.length} proposal item(s) imported successfully!`);
        } catch (err) {
            console.error('Failed to import proposal items:', err);
            showAlert('danger', 'Failed to import proposal items. Please try again.');
        } finally {
            setIsImportingProposal(false);
        }
    };

    const handleSendInvoice = () => {
        if (!invoice) return;
        
        // Check permission
        if (!canInvoice('send_email')) {
            showAlert('danger', 'You do not have permission to send invoices via email.');
            return;
        }
        
        setShowSendConfirmation(true);
    };

    const confirmSendInvoice = () => {
        setShowSendConfirmation(false);
        sendInvoiceToClient();
    };

    // Load email settings and templates
    useEffect(() => {
        const loadEmailData = async () => {
            try {
                const settings = await getEmailSettings();
                setEmailSettings(settings);
            } catch (e) {
                setEmailSettings(null);
            }
            try {
                const templates = await getInvoiceEmailTemplates();
                setEmailTemplates(templates);
            } catch (e) {
                setEmailTemplates([]);
            }
        };
        loadEmailData();
    }, []);

    const sendInvoiceToClient = async () => {
        try {
            setIsSendingEmail(true);

            // ✅ SEND EMAIL: Langsung send email tanpa update invoice
            // Send email tidak perlu update data, cukup kirim email dengan data yang ada

            // Reload if missing
            let currentSettings = emailSettings;
            let currentTemplates = emailTemplates;
            if (!currentSettings) {
                currentSettings = await getEmailSettings();
                setEmailSettings(currentSettings);
            }
            if (!currentTemplates || currentTemplates.length === 0) {
                currentTemplates = await getInvoiceEmailTemplates();
                setEmailTemplates(currentTemplates);
            }

            // ✅ SEND TO FINANCE: Backend always sends to finance email from application.properties
            // Client email is not required when sending to finance, but we can still try to get it for reference
            let clientEmail = invoice.clientEmail || invoice.email || invoice.client_email || '';
            if (!clientEmail && invoice?.id) {
                clientEmail = await getInvoiceClientEmail(invoice.id);
            }
            // Note: Even if clientEmail is empty, backend will still send to finance email
            // So we don't need to block sending if client email is not found

            // Find invoice template
            let template = null;
            if (Array.isArray(currentTemplates) && currentTemplates.length > 0) {
                template = currentTemplates.find(t => t.templateId === 'invoice' || t.id === 'invoice') || currentTemplates[0];
            }

            // Defaults
            let subject = `Invoice ${invoice.invoiceNumber || ''}`.trim();

            // ✅ ADD PIC INFO: Include PIC info in default message
            let picInfo = '';
            if (invoice.picName || invoice.picEmail) {
                picInfo = '\n\nAttention to:\n';
                if (invoice.picName) {
                    picInfo += `Name: ${invoice.picName}\n`;
                }
                if (invoice.picEmail) {
                    picInfo += `Email: ${invoice.picEmail}\n`;
                }
            }
            
            let message = `Dear ${invoice.clientName || 'Client'},${picInfo}\n\nPlease find your invoice ${invoice.invoiceNumber || ''}.\n\nThank you.\n\nBest regards,\n${invoice.createdByName || invoice.createdBy || ''}`;

            // Helper: format dates as dd/mm/yyyy
            const formatDateToDDMMYYYY = (value) => {
                if (!value) return '';
                const d = new Date(value);
                if (Number.isNaN(d.getTime())) return value;
                const dd = String(d.getDate()).padStart(2, '0');
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const yyyy = d.getFullYear();
                return `${dd}/${mm}/${yyyy}`;
            };

            // Helper to replace placeholders in both subject and content
            // ✅ Use total from database (raw value, no frontend calculation)
            const currentTotal = invoice.total || 0;
            
            const replacePlaceholders = (text) => (text || '')
                .replace(/\{\{invoice_number\}\}/g, invoice.invoiceNumber || '')
                .replace(/\{\{client_name\}\}/g, invoice.clientName || 'Client')
                .replace(/\{\{proposal_code\}\}/g, invoice.proposalCode || invoice.proposal_code || '')
                .replace(/\{\{description\}\}/g, invoice.description || '')
                .replace(/\{\{issue_date\}\}/g, formatDateToDDMMYYYY(invoice.billDate))
                .replace(/\{\{due_date\}\}/g, formatDateToDDMMYYYY(invoice.dueDate))
                .replace(/\{\{amount_due\}\}/g, `${getCurrencyPrefix(invoiceCurrencyCode)}${Number(currentTotal).toLocaleString('id-ID')}`)
                .replace(/\{\{invoice_notes\}\}/g, invoice.message || invoice.invoice_notes || '')
                .replace(/\{\{message\}\}/g, invoice.message || invoice.invoice_notes || '')
                .replace(/\{\{payment_method\}\}/g, invoice.paymentMethod || '')
                .replace(/\{\{company_name\}\}/g, '')
                .replace(/\{\{company_email\}\}/g, 'finance@cbqaglobal.com')
                // ✅ ADD PIC INFO: Replace PIC name and email placeholders
                .replace(/\{\{pic_name\}\}/g, invoice.picName || '')
                .replace(/\{\{pic_email\}\}/g, invoice.picEmail || '')
                // ✅ ADD CREATOR INFO: Replace creator name placeholder
                .replace(/\{\{creator_name\}\}/g, invoice.createdByName || invoice.createdBy || '')
                // ✅ ADD ASSIGNED USER: Replace assigned user name placeholder (fallback to creator if not available)
                .replace(/\{\{assigned_user_name\}\}/g, invoice.assignedUserName || invoice.assigned_user_name || invoice.createdByName || invoice.createdBy || '');

            if (template) {
                subject = replacePlaceholders(template.subject || subject);
                const content = replacePlaceholders(template.content || message);
                message = content;
            } else {
                // Ensure defaults also get placeholder replacement
                subject = replacePlaceholders(subject);
                message = replacePlaceholders(message);
            }

            // ✅ SEND TO FINANCE: Backend uses defaultInvoiceEmailTo from application.properties
            // The 'to' field is passed but backend will use finance email regardless
            const emailData = {
                to: clientEmail || '', // Pass empty string if client email not found - backend will use finance email
                subject,
                message,
                invoiceId: invoice.id,
                includeAttachment: true
            };

            await sendInvoiceEmail(emailData);
            try {
                await markInvoiceSentToClient(invoice.id);
                // Refresh invoice to reflect status and lastEmailSent
                await fetchInvoiceDetail();
            } catch (markErr) {
                console.warn('Failed to mark invoice as SENT_TO_CLIENT:', markErr);
            }
            showAlert('success', 'Invoice sent to client successfully!');
        } catch (e) {
            console.error('Error sending invoice:', e);
            showAlert('danger', 'Failed to send invoice. Please try again.');
        } finally {
            setIsSendingEmail(false);
        }
    };

    const handleDownload = async () => {
        try {
            // Show loading state
            const downloadButton = document.querySelector('button[onClick="handleDownload"]');
            const originalText = downloadButton?.innerHTML;
            if (downloadButton) {
                downloadButton.innerHTML = `
                    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-loader me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                        <path d="M12 6l0 -3"></path>
                        <path d="M16.25 7.75l2.15 -2.15"></path>
                        <path d="M18 12l3 0"></path>
                        <path d="M16.25 16.25l2.15 2.15"></path>
                        <path d="M12 18l0 3"></path>
                        <path d="M7.75 16.25l-2.15 2.15"></path>
                        <path d="M6 12l-3 0"></path>
                        <path d="M7.75 7.75l-2.15 -2.15"></path>
                    </svg>
                    Downloading...
                `;
                downloadButton.disabled = true;
            }

            // ✅ DOWNLOAD: Langsung download PDF tanpa update invoice
            // Download tidak perlu update data, cukup generate PDF dari data yang ada

            // Call the PDF API endpoint
            const response = await api.get(`/crm/invoices/pdf/${id}/pdf`, {
                responseType: 'blob' // Important: set responseType to blob for file download
            });

            // Create a blob URL and trigger download
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            // Create a temporary link element and trigger download
            const link = document.createElement('a');
            link.href = url;
            link.download = `${invoice.invoiceNumber}.pdf`;
            document.body.appendChild(link);
            link.click();

            // Clean up
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            showAlert('success', 'Invoice PDF downloaded successfully!');

        } catch (error) {
            console.error('Error downloading PDF:', error);
            showAlert('danger', 'Failed to download PDF. Please try again.');
        } finally {
            // Restore button state
            const downloadButton = document.querySelector('button[onClick="handleDownload"]');
            if (downloadButton) {
                downloadButton.innerHTML = `
                    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-download me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                        <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2"></path>
                        <path d="M7 11l5 5l5 -5"></path>
                        <path d="M12 4l0 12"></path>
                    </svg>
                    Download
                `;
                downloadButton.disabled = false;
            }
        }
    };

    const handlePrint = () => {
        // TODO: Implement print functionality
        window.print();
    };

    const handleDownloadUploadedPdf = async () => {
        try {
            if (!invoice?.id) {
                showAlert('danger', 'Invoice ID not found');
                return;
            }

            if (!invoice?.documentPath) {
                showAlert('warning', 'No uploaded PDF found for this invoice.');
                return;
            }

            // Use the new API endpoint to download the uploaded PDF
            const response = await api.get(`/crm/invoices/${invoice.id}/uploaded-pdf`, {
                responseType: 'blob' // Important: set responseType to blob for file download
            });

            // Create a blob URL and trigger download
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            // Create a temporary link element and trigger download
            const link = document.createElement('a');
            link.href = url;
            link.download = `${invoice.invoiceNumber}_uploaded.pdf`;
            document.body.appendChild(link);
            link.click();

            // Clean up
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            showAlert('success', 'Uploaded PDF downloaded successfully!');

        } catch (error) {
            console.error('Error downloading uploaded PDF:', error);
            if (error.response?.status === 404) {
                showAlert('warning', 'No uploaded PDF found for this invoice.');
            } else {
                showAlert('danger', 'Failed to download uploaded PDF. Please try again.');
            }
        }
    };

    const handleEdit = () => {
        // ✅ Initialize formData from invoice when opening edit modal (same as Invoice/index.jsx)
        setFormData({
            client: invoice.companyId || invoice.client || '',
            projectName: invoice.projectId || invoice.projectName || '',
            projectId: invoice.projectId || null,
            leadId: invoice.leadId || null,
            billDate: invoice.billDate || new Date().toISOString().split('T')[0],
            dueDate: invoice.dueDate || '',
            description: invoice.description || '',
            jurnalNumber: invoice.jurnalNumber || '',
            invoiceTo: invoice.invoiceTo || '',
            invoiceIssued: invoice.invoiceIssued || '',
            tax: invoice.taxPercentage || invoice.tax || 0,
            applyTax: invoice.applyTax || false,
            documents: [],
            subtotal: invoice.subtotal || 0,
            dppLainnya: invoice.dppLainnya || 0,
            amountReceived: invoice.amountReceived || 0,
            message: invoice.message || '',
            notes: invoice.notes || '',
            terms: invoice.terms || '',
            paymentMethod: invoice.paymentMethod || '',
            bankAccount: invoice.bankAccount || ''
        });
        setFormErrors({});
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setFormErrors({}); // Clear form errors when modal closes (same as Invoice/index.jsx)
        // ✅ REFRESH DATA SETELAH MODAL DITUTUP
        fetchInvoiceDetail();
    };

    // ✅ CALCULATION: Helper function to recalculate totals
    const recalculateTotals = (invoiceData) => {
        const subtotal = invoiceData.subtotal || 0;
        const dppLainnya = invoiceData.dppLainnya || 0;
        const taxPercentage = invoiceData.tax || invoiceData.taxPercentage || 0;
        const applyTax = invoiceData.applyTax || false;
        
        // Calculate PPN
        let calculatedPPN = 0;
        // ✅ FIX: If tax percentage is 0, PPN should always be 0
        if (taxPercentage === 0 || taxPercentage === null) {
            calculatedPPN = 0;
        } else if (applyTax && subtotal > 0 && taxPercentage > 0) {
            const ppnCalculated = subtotal * (taxPercentage / 100);
            calculatedPPN = Math.ceil(ppnCalculated); // Always round up
        } else {
            // If not applying tax or subtotal is 0, PPN should be 0
            calculatedPPN = 0;
        }
        
        // Calculate total
        const total = subtotal + dppLainnya + calculatedPPN;
        
        // Calculate balance due
        const amountReceived = invoiceData.amountReceived || 0;
        const balanceDue = total - amountReceived;
        
        return {
            ppn: calculatedPPN,
            total: total,
            balanceDue: balanceDue
        };
    };

    const handleFormSubmit = async (e, onSuccess, formDataFromChild = null) => {
        e.preventDefault();
        setIsSubmitting(true);

        // ✅ Use form data from child component if provided, otherwise use local formData
        const dataToValidate = formDataFromChild || formData;

        // ✅ Validate form using the validation utility (same as Invoice/index.jsx)
        const validationData = {
            ...dataToValidate,
            isEditMode: true // Always edit mode in InvoiceView
        };
        const errors = validateInvoiceForm(validationData);

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            setIsSubmitting(false);
            return;
        }

        try {
            if (!invoice?.id) {
                console.error('No invoice ID found');
                setIsSubmitting(false);
                return;
            }

            // Clear any existing errors
            setFormErrors({});

            // ✅ FIX: Prioritas projectId untuk edit, projectName untuk create (same as Invoice/index.jsx)
            const projectIdToSend = dataToValidate.projectId || dataToValidate.projectName || invoice.projectId;

            // ✅ CALCULATION: Send only raw values to backend - let backend calculate totals
            // Frontend calculation is only for UI preview, backend is source of truth
            const subtotal = invoice.subtotal || 0;
            const taxPercentage = dataToValidate.tax || 0;
            const applyTax = dataToValidate.applyTax !== undefined ? dataToValidate.applyTax : (invoice.applyTax || false);

            // ✅ FIX STATUS: Don't send status when editing (backend preserves it)
            const payload = {
                companyId: dataToValidate.client || invoice.companyId || invoice.client,
                // ✅ PROJECT RELATION: Send projectId dengan prioritas yang benar
                projectId: projectIdToSend,
                billDate: dataToValidate.billDate || invoice.billDate,
                dueDate: dataToValidate.dueDate || invoice.dueDate,
                description: dataToValidate.description || invoice.description,
                jurnalNumber: dataToValidate.jurnalNumber ?? invoice.jurnalNumber ?? '',
                invoiceTo: dataToValidate.invoiceTo ?? invoice.invoiceTo ?? '',
                invoiceIssued: dataToValidate.invoiceIssued ?? invoice.invoiceIssued ?? '',
                taxPercentage: taxPercentage,
                applyTax: applyTax,
                subtotal: subtotal,
                dppLainnya: invoice.dppLainnya || 0,
                // ✅ Don't send ppn, total, balanceDue - backend will calculate them
                amountReceived: invoice.amountReceived || 0,
                message: invoice.message || '',
                notes: invoice.notes || '',
                terms: invoice.terms || '',
                paymentMethod: invoice.paymentMethod || '',
                bankAccount: invoice.bankAccount || ''
            };

            // Get file from form data documents or uploadedFile state
            const file = dataToValidate.documents?.[0]?.file || uploadedFile;

            // Validate file size before upload (guard against server MULTIPART_ERROR)
            const MAX_FILE_SIZE_MB = 5;
            if (file && file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
                setAlert({
                    type: 'danger',
                    message: `File terlalu besar (${(file.size / 1024 / 1024).toFixed(1)} MB). Maksimal ukuran file adalah ${MAX_FILE_SIZE_MB} MB. Harap kompres PDF terlebih dahulu (gunakan ilovepdf.com atau sejenisnya).`
                });
                return;
            }

            let response;
            if (file) {
                // Send as flat form fields (not JSON blob) — matches Spring Boot @RequestParam pattern
                const fd = new FormData();
                Object.entries(payload).forEach(([key, val]) => {
                    if (val !== null && val !== undefined) fd.append(key, val);
                });
                fd.append('file', file, file.name);
                response = await api.put(`/crm/invoices/${invoice.id}`, fd, {
                    maxBodyLength: Infinity,
                    maxContentLength: Infinity,
                });
            } else {
                // No file — send plain JSON to avoid touching temp upload directory
                response = await api.put(`/crm/invoices/${invoice.id}`, payload);
            }

            if (response.data.success) {
                setUploadedFile(null);
                setIsModalOpen(false);
                await fetchInvoiceDetail();
                showAlert('success', 'Invoice updated successfully!');
                if (onSuccess) {
                    onSuccess();
                }
            } else {
                console.error('❌ BACKEND ERROR: Error saving invoice:', response.data.error || 'Failed to save invoice');
                showAlert('danger', response.data.error || 'Failed to update invoice. Please try again.');
            }
            setIsSubmitting(false);
        } catch (error) {
            console.error('❌ FRONTEND ERROR: Error submitting form:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                statusText: error.response?.statusText
            });

            // Show specific error message from backend if available
            const errorMessage = error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                'Failed to submit invoice. Please try again.';

            showAlert('danger', errorMessage);
            setIsSubmitting(false);
        }
    };

    const handleCoaChange = async (itemId, coaId) => {
        setSavingCoaItemId(itemId);
        try {
            await api.patch(`/crm/invoices/${id}/items/${itemId}/coa`, {
                chartOfAccountId: coaId ? Number(coaId) : null
            });
            await fetchInvoiceDetail();
        } catch (e) {
            showAlert('danger', 'Failed to update Chart of Account');
        } finally {
            setSavingCoaItemId(null);
        }
    };

    const handleAddItem = () => {
        setEditingItem(null);
        setIsItemModalOpen(true);
    };

    const handleEditItem = (item, index) => {
        // Transform item data untuk form
        const editingItemData = {
            id: item.id,
            description: item.description || '',
            quantity: item.quantity || 1,
            unitPrice: item.unitPrice || item.rate || 0,
            discount: item.discount || 0,
            taxed: item.isTaxed || false,
            currencyCode: item.currencyCode || 'IDR',
            exchangeRateAtCreation: item.exchangeRateAtCreation || 1,
            index: index
        };

        setEditingItem(editingItemData);
        setIsItemModalOpen(true);
    };

    const handleItemSubmit = async (itemData) => {
        try {
            // ✅ REFRESH INVOICE DATA AFTER ITEM IS SAVED
            await fetchInvoiceDetail();

            // Close modal
            setIsItemModalOpen(false);
            setEditingItem(null);

        } catch (error) {
            console.error('Error refreshing invoice data:', error);
        }
    };

    const handleDeleteItem = (item) => {
        setItemToDelete(item);
        setShowDeleteItemModal(true);
    };

    const confirmDeleteItem = async () => {
        try {
            // Call API to delete item
            await api.delete(`/crm/invoice-items/${itemToDelete.id}`);

            showAlert('success', 'Item deleted successfully!');

            // Refresh invoice data to reflect changes
            await fetchInvoiceDetail();

            // Close modal and reset state
            setShowDeleteItemModal(false);
            setItemToDelete(null);

        } catch (error) {
            console.error('Error deleting item:', error);
            showAlert('danger', 'Failed to delete item. Please try again.');
            setShowDeleteItemModal(false);
            setItemToDelete(null);
        }
    };

    const handleFinalizeInvoice = () => {
        // Check permission
        if (!canInvoice('can_finalize')) {
            showAlert('danger', 'You do not have permission to finalize invoices.');
            return;
        }
        
        // Check if already final
        if (invoice?.status === 'FINAL') {
            showAlert('warning', 'This invoice is already finalized.');
            return;
        }

        setShowFinalizeModal(true);
    };

    const confirmFinalizeInvoice = async () => {
        try {
            // Call API to finalize invoice
            await api.put(`/crm/invoices/${invoice.id}/finalize`);

            showAlert('success', 'Invoice finalized successfully!');

            // Refresh invoice data to reflect changes
            await fetchInvoiceDetail();

            // Close modal
            setShowFinalizeModal(false);

        } catch (error) {
            console.error('Error finalizing invoice:', error);
            const errorMessage = error.response?.data?.error ||
                error.response?.data?.message ||
                'Failed to finalize invoice. Please try again.';
            showAlert('danger', errorMessage);
            setShowFinalizeModal(false);
        }
    };

    const handlePayment = async () => {
        // Check if invoice status is FINAL or PARTIAL (both can receive payments)
        if (invoice?.status !== 'FINAL' && invoice?.status !== 'PARTIAL') {
            showAlert('warning', 'Only invoices with FINAL or PARTIAL status can receive payments.');
            return;
        }
        
        // Prevent payment if already PAID
        if (invoice?.status === 'PAID') {
            showAlert('warning', 'This invoice is already paid.');
            return;
        }
        
        // Get client ID and invoice ID
        const clientId = invoice.companyId || invoice.client || '';
        const invoiceId = invoice.id || '';
        
        // Pre-fill payment form with invoice data
        setPaymentFormData({
            client: clientId,
            invoice: invoiceId,
            paymentMethod: '',
            paymentDate: new Date().toISOString().split('T')[0],
            amount: invoice.balanceDue || invoice.total || '',
            note: '',
            file: null
        });
        
        // Open payment modal
        setIsPaymentModalOpen(true);
    };

    const handlePaymentInputChange = useCallback((name, value) => {
        setPaymentFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error when user starts typing
        setPaymentFormErrors(prev => {
            if (prev[name]) {
                return {
                    ...prev,
                    [name]: ''
                };
            }
            return prev;
        });
    }, []);

    const handlePaymentFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        
        try {
            // Validate form
            const errors = {};
            if (!paymentFormData.client) errors.client = 'Client is required';
            if (!paymentFormData.invoice) errors.invoice = 'Invoice is required';
            if (!paymentFormData.paymentMethod) errors.paymentMethod = 'Payment method is required';
            if (!paymentFormData.paymentDate) errors.paymentDate = 'Payment date is required';
            
            // Extract numeric amount
            let numericAmount = 0;
            if (paymentFormData.amount) {
                if (typeof paymentFormData.amount === 'string') {
                    numericAmount = parseFloat(paymentFormData.amount.replace(/[^\d.-]/g, ''));
                } else {
                    numericAmount = parseFloat(paymentFormData.amount);
                }
            }
            
            if (isNaN(numericAmount) || numericAmount <= 0) {
                errors.amount = 'Amount must be greater than 0';
            }
            
            if (Object.keys(errors).length > 0) {
                setPaymentFormErrors(errors);
                return;
            }
            
            // ✅ Prepare FormData for API - send as form fields, not JSON blob
            const data = new FormData();
            data.append('clientId', paymentFormData.client);
            data.append('invoiceId', paymentFormData.invoice);
            data.append('paymentMethod', paymentFormData.paymentMethod);
            data.append('paymentDate', paymentFormData.paymentDate);
            data.append('amount', numericAmount);
            data.append('note', paymentFormData.note || '');
            
            // Add file if exists (backend expects 'document' field name)
            if (paymentFormData.file && paymentFormData.file instanceof File) {
                data.append('document', paymentFormData.file);
            }
            
            // Submit payment
            const response = await api.post('/crm/payments', data, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            if (response.data) {
                showAlert('success', 'Payment added successfully!');
                setIsPaymentModalOpen(false);
                
                // Reset form
                setPaymentFormData({
                    client: '',
                    invoice: '',
                    paymentMethod: '',
                    paymentDate: new Date().toISOString().split('T')[0],
                    amount: '',
                    note: '',
                    file: null
                });
                setPaymentFormErrors({});
                
                // Refresh invoice data
                await fetchInvoiceDetail();
            }
        } catch (error) {
            console.error('Error creating payment:', error);
            const errorMessage = error.response?.data?.message || 
                                error.response?.data?.error || 
                                'Failed to create payment. Please try again.';
            showAlert('danger', errorMessage);
        }
    }, [paymentFormData, showAlert, fetchInvoiceDetail]);

    // Currency name map for "amount in words" suffix
    const CURRENCY_UNIT_NAMES = {
        IDR: { main: 'rupiah', fraction: 'sen' },
        USD: { main: 'dollar', fraction: 'cent' },
        EUR: { main: 'euro', fraction: 'cent' },
        GBP: { main: 'pound', fraction: 'pence' },
        JPY: { main: 'yen', fraction: null },
        SGD: { main: 'dollar', fraction: 'cent' },
        AUD: { main: 'dollar', fraction: 'cent' },
    };

    // English number-to-words for non-IDR currencies
    const numberToWordsEN = (number) => {
        if (number === 0) return '';
        const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
                      'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
                      'seventeen', 'eighteen', 'nineteen'];
        const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
        const scales = ['', 'thousand', 'million', 'billion', 'trillion'];

        const convertChunk = (n) => {
            if (n === 0) return '';
            if (n < 20) return ones[n];
            if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
            return ones[Math.floor(n / 100)] + ' hundred' + (n % 100 ? ' ' + convertChunk(n % 100) : '');
        };

        let result = '';
        let scaleIndex = 0;
        let remaining = Math.floor(number);
        while (remaining > 0) {
            const chunk = remaining % 1000;
            if (chunk !== 0) {
                const chunkWords = convertChunk(chunk);
                result = chunkWords + (scales[scaleIndex] ? ' ' + scales[scaleIndex] : '') + (result ? ' ' + result : '');
            }
            remaining = Math.floor(remaining / 1000);
            scaleIndex++;
        }
        return result;
    };

    const calculateAmountInWords = (total) => {
        // Invoice totals are persisted in base currency (IDR), regardless of
        // the proposal's original display currency.
        const currencyCode = 'IDR';
        if (!total || total === 0) {
            const unitName = CURRENCY_UNIT_NAMES[currencyCode];
            const zeroText = currencyCode === 'IDR' ? 'nol rupiah' : `zero ${unitName?.main || currencyCode.toLowerCase()}`;
            return zeroText.charAt(0).toUpperCase() + zeroText.slice(1);
        }

        if (currencyCode === 'IDR') {
            const idrText = numberToWords(total);
            return idrText.charAt(0).toUpperCase() + idrText.slice(1);
        }

        const unitName = CURRENCY_UNIT_NAMES[currencyCode] || { main: currencyCode.toLowerCase(), fraction: null };
        const integerPart = Math.floor(total);
        const decimalPart = Math.round((total - integerPart) * 100);

        let result = numberToWordsEN(integerPart);
        if (result) result += ' ' + unitName.main;

        if (decimalPart > 0 && unitName.fraction) {
            result += ' and ' + numberToWordsEN(decimalPart) + ' ' + unitName.fraction;
        }

        result = result || `zero ${unitName.main}`;
        return result.charAt(0).toUpperCase() + result.slice(1);
    };

    // Function untuk menghitung tax amount untuk item
    const calculateItemTaxAmount = (item) => {
        if (!item.isTaxed || !invoice.taxPercentage) return 0;
        
        const unitPrice = item.unitPrice || 0;
        const quantity = item.quantity || 1;
        const discount = item.discount || 0;
        
        // Calculate subtotal after discount
        const subtotal = (unitPrice * quantity) - discount;
        
        // Calculate tax amount
        const taxAmount = subtotal * (invoice.taxPercentage / 100);
        
        return taxAmount;
    };

    // Function untuk menghitung total amount untuk item (subtotal + tax)
    const calculateItemTotalAmount = (item) => {
        const unitPrice = item.unitPrice || 0;
        const quantity = item.quantity || 1;
        const discount = item.discount || 0;
        const taxAmount = calculateItemTaxAmount(item);
        
        // Calculate subtotal after discount
        const subtotal = (unitPrice * quantity) - discount;
        
        // Total amount = subtotal + tax
        const totalAmount = subtotal + taxAmount;
        
        return totalAmount;
    };

    // Function untuk menghitung total tax dari semua items
    const calculateTotalTaxFromItems = () => {
        if (!invoice?.items) return 0;
        
        return invoice.items.reduce((totalTax, item) => {
            return totalTax + calculateItemTaxAmount(item);
        }, 0);
    };

    // ✅ NEW: Function untuk menghitung PPN berdasarkan Apply Tax
    const calculatePPN = () => {
        // Jika Apply Tax ter-checklist, calculate PPN = subtotal * tax_percentage (always rounded up)
        if (invoice?.applyTax) {
            const subtotal = invoice.subtotal || 0;
            const taxPercentage = invoice.taxPercentage || invoice.tax || 0;
            const ppnCalculated = subtotal * (taxPercentage / 100);
            const ppn = Math.ceil(ppnCalculated); // Always round up
            
            return ppn;
        }
        
        // Jika Apply Tax tidak ter-checklist, return PPN from database table
        const ppnFromTable = invoice?.ppn || 0;
        
        return ppnFromTable;
    };

    // Function untuk menghitung total amount dari semua items
    const calculateTotalAmountFromItems = () => {
        if (!invoice?.items) return 0;
        
        return invoice.items.reduce((totalAmount, item) => {
            return totalAmount + calculateItemTotalAmount(item);
        }, 0);
    };

    // ✅ MULTI-CURRENCY: Get effective currency code - use invoice currency if available, otherwise company currency
    const getEffectiveCurrencyCode = () => {
        if (invoice) {
            const invoiceCurrency = invoice.displayCurrencyCode || 
                                   invoice.currencyCode || 
                                   invoice.invoiceCurrencyCode;
            if (invoiceCurrency) {
                return invoiceCurrency;
            }
        }
        return invoiceCurrencyCode; // Fallback to company currency
    };

    const getRawItemTotal = (item) => {
        if (item?.total != null) return Number(item.total) || 0;
        return (Number(item?.unitPrice || 0) * Number(item?.quantity || 1)) - Number(item?.discount || 0);
    };

    const getItemIdrTotal = (item) => {
        const raw = getRawItemTotal(item);
        const code = (item?.currencyCode || 'IDR').toUpperCase();
        const rate = Number(item?.exchangeRateAtCreation || invoice?.exchangeRateAtCreation || 1) || 1;
        return code === 'IDR' ? raw : raw * rate;
    };

    const formatInvoiceItemTotal = (item) => {
        const baseText = formatCurrency(getItemIdrTotal(item), 'IDR');
        const code = (item?.currencyCode || 'IDR').toUpperCase();
        if (code === 'IDR') return baseText;
        const sourceTotal = getRawItemTotal(item);
        return <><span>{formatCurrency(sourceTotal, code)}</span><small className="text-muted ms-2">= {baseText}</small></>;
    };

    const getSourceCurrencySummary = () => {
        const codes = [...new Set((invoice?.items || []).map(item => (item.currencyCode || 'IDR').toUpperCase()))];
        return codes.length === 1 && codes[0] !== 'IDR' ? codes[0] : null;
    };

    const getSourceSubtotal = () => (invoice?.items || []).reduce((sum, item) =>
        sum + getRawItemTotal(item), 0);

    const getIdrSubtotal = () => (invoice?.items || []).reduce((sum, item) => sum + getItemIdrTotal(item), 0);

    const getSourceExchangeRate = () => {
        const sourceCode = getSourceCurrencySummary();
        const item = (invoice?.items || []).find(row => (row.currencyCode || '').toUpperCase() === sourceCode);
        return item?.exchangeRateAtCreation || 1;
    };

    const formatSummaryAmount = (amount, sourceCode, rate) => {
        const raw = Number(amount || 0);
        const code = (sourceCode || 'IDR').toUpperCase();
        const idr = code === 'IDR' ? raw : raw * (Number(rate) || 1);
        if (code === 'IDR') return formatCurrency(idr, 'IDR');
        return <>{formatCurrency(raw, code)} / {formatCurrency(idr, 'IDR')}</>;
    };

    // Status badge styling function (same as main Invoice page)
    const getStatusBadge = (status) => {
        const colors = {
            'PAID': 'bg-success text-white',
            'UNPAID': 'bg-warning text-white',
            'OVERDUE': 'bg-danger text-white',
            'PARTIAL': 'bg-info text-white',
            'DRAFT': 'badge-outline text-orange',
            'SENT': 'badge-outline text-blue',
            'CANCELLED': 'badge-outline text-gray',
            'FINAL': 'bg-primary text-white'
        };
        return `badge ${colors[status] || 'badge-outline text-gray'}`;
    };

    if (loading) {
        return (
            <div className="container-xl">
                <div className="text-center py-4">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-xl">
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            </div>
        );
    }

    if (!invoice) {
        return (
            <div className="container-xl">
                <div className="alert alert-warning" role="alert">
                    Invoice not found
                </div>
            </div>
        );
    }

    return (
        <div className="container-xl invoice-detail-page">
            {/* Alert Component */}
            {alert && (
                <Alert
                    type={alert.type}
                    message={alert.message}
                    onClose={() => setAlert(null)}
                />
            )}

            <div className="page-header d-print-none">
                <div className="row align-items-center">
                    <div className="col">
                        <div className="d-flex align-items-center">
                            <div className="d-flex align-items-center">
                                <a className="btn btn-outline-white d-none d-sm-inline-block me-3" onClick={handleBack}>
                                    <ArrowLeft className="icon icon-tabler icon-tabler-arrow-left" />
                                    Back
                                </a>
                                <h3 className="page-title mb-0">Invoice Details</h3>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row align-items-center">
                <div className="col-md-8 mt-3 mb-0">
                    <h3 className="mb-2">{invoice.projectName}</h3>
                    <p className="text-muted mb-0">{invoice.clientName}</p>
                </div>
            </div>

            <div className="row mt-3">
                <div className="col-md-9">
                    {/* Left side content */}
                    <div className="card">
                        <div className="card-body">
                            {/* Company Header */}
                            <div className="row mb-4">
                                <div className="col-12">
                                    <img src="/static/logo.png" alt="Company Logo" style={{ height: "50px" }} className="mb-3" />
                                </div>
                                <div className="col-8">
                                    <div>
                                        <h3 className="mb-0">PT CBQA GLOBAL INDONESIA</h3>
                                        <div className="text-muted">South Quarter Tower A</div>
                                        <div className="text-muted">17th floor Unit B2 - C1, Jl. RA. Kartini Kav. 8</div>
                                        <div className="text-muted">Cilandak, Jakarta Selatan 12430</div>
                                        <div className="text-muted">021-27814200</div>
                                        <div className="text-muted">finance@cbqaglobal.com</div>
                                        <div className="text-muted">NPWP : 0968402698017000</div>
                                    </div>
                                </div>
                                <div className="col-4 text-end mb-4">
                                    <div><strong>INVOICE #</strong> : {invoice.invoiceNumber}</div>
                                    <div><strong>DATE</strong> : {invoice.billDate}</div>
                                </div>
                            </div>


                            {/* Client Details */}
                            <div className="row mb-4">
                                <div className="col-12">
                                    <div className="table-responsive">
                                        <table className="table table-borderless">
                                            <tbody>
                                                <tr>
                                                    <td style={{ width: "150px" }}><strong>Name:</strong></td>
                                                    <td>{invoice.clientName}</td>
                                                    <td style={{ width: "150px" }}><strong>Order No:</strong></td>
                                                    <td>{invoice.proposalCode || invoice.proposal_code || ''}</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Address:</strong></td>
                                                    <td>{invoice.address}</td>
                                                    <td><strong>Due Date:</strong></td>
                                                    <td>{invoice.dueDate}</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Phone:</strong></td>
                                                    <td>{invoice.phone}</td>
                                                    <td></td>
                                                    <td></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            {/* ✅ PROPOSAL ITEMS TABLE */}
                            {invoice?.projectId && proposalBreakdown && invoice.status !== 'PAID' && !hasImportedProposal && (
                                <ProposalItemsTable
                                    breakdown={proposalBreakdown}
                                    rows={proposalRows}
                                    onRowsChange={setProposalRows}
                                    currencyCode={proposalRows[0]?.currencyCode || 'IDR'}
                                    onImport={handleImportProposalItems}
                                    isImporting={isImportingProposal}
                                    hasImported={hasImportedProposal}
                                    isProjectBased={true}
                                />
                            )}

                            {/* Items Table */}
                            <div className="table-responsive">
                                <table className="table table-vcenter card-table">
                                    <thead>
                                        <tr>
                                            <th>Description</th>
                                            <th>Total</th>
                                            {isFinanceOrAdmin && <th style={{ minWidth: 180 }}>Chart of Account</th>}
                                            <th className="w-1"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoice.items?.map((item, index) => (
                                            <tr key={item.id || index}>
                                                <td>{item.description}</td>
                                                <td>{formatInvoiceItemTotal(item)}</td>
                                                {isFinanceOrAdmin && (
                                                    <td>
                                                        <FormFieldSelect className="app-select--sm"
                                                            value={item.chartOfAccountId || ''}
                                                            onChange={e => handleCoaChange(item.id, e.target.value || null)}
                                                            disabled={savingCoaItemId === item.id}
                                                            style={{ minWidth: 160 }}
                                                        >
                                                            <option value="">— No COA —</option>
                                                            {coaOptions.map(coa => (
                                                                <option key={coa.id} value={coa.id}>
                                                                    {coa.accountCode} - {coa.accountName}
                                                                </option>
                                                            ))}
                                                        </FormFieldSelect>
                                                        {savingCoaItemId === item.id && (
                                                            <small className="text-muted ms-1">Saving...</small>
                                                        )}
                                                    </td>
                                                )}
                                                <td>
                                                    <div className="btn-group">
                                                        <button
                                                            type="button"
                                                            className="btn btn-icon btn-outline-primary"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                handleEditItem(item, index);
                                                            }}
                                                            title="Edit Item"
                                                            disabled={invoice?.status === 'PAID'}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-edit" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                                                <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1"></path>
                                                                <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z"></path>
                                                                <path d="M16 5l3 3"></path>
                                                            </svg>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-icon btn-outline-danger"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                handleDeleteItem(item);
                                                            }}
                                                            title="Delete Item"
                                                            disabled={invoice?.status === 'PAID'}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-trash" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                                                <path d="M4 7l16 0"></path>
                                                                <path d="M10 11l0 6"></path>
                                                                <path d="M14 11l0 6"></path>
                                                                <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"></path>
                                                                <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"></path>
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Add Item Button */}
                            <div className="mt-4">
                                <button className="btn btn-outline-primary" onClick={handleAddItem} disabled={invoice?.status === 'PAID'}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-plus" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                        <path d="M12 5l0 14"></path>
                                        <path d="M5 12l14 0"></path>
                                    </svg>
                                    Add Item
                                </button>
                            </div>

                            {/* Summary */}
                            <div className="mt-4">
                                <div className="row">
                                    <div className="col-md-6 offset-md-6">
                                        <div className="mb-3 d-flex justify-content-between">
                                            <span>Sub Total:</span>
                                            <strong>
                                                {getSourceCurrencySummary()
                                                    ? formatSummaryAmount(getSourceSubtotal(), getSourceCurrencySummary(), getSourceExchangeRate())
                                                    : formatCurrency(getIdrSubtotal(), 'IDR')}
                                            </strong>
                                        </div>
                                        {getSourceCurrencySummary() && (
                                            <div className="text-muted small text-end mb-3">
                                                1 {getSourceCurrencySummary()} = Rp {Number(getSourceExchangeRate()).toLocaleString('id-ID')}
                                            </div>
                                        )}
                                        <div className="mb-3 d-flex justify-content-between">
                                            <span>DPP Lainnya:</span>
                                            <strong>{formatSummaryAmount(invoice.dppLainnya, getSourceCurrencySummary(), getSourceExchangeRate())}</strong>
                                        </div>
                                        <div className="mb-3 d-flex justify-content-between">
                                            <span>PPN:</span>
                                            <strong>{formatSummaryAmount(invoice.ppn || 0, getSourceCurrencySummary(), getSourceExchangeRate())}</strong>
                                        </div>
                                        <div className="mb-3 d-flex justify-content-between">
                                            <span>Total:</span>
                                            <strong>{formatSummaryAmount(invoice.total || 0, getSourceCurrencySummary(), getSourceExchangeRate())}</strong>
                                        </div>
                                        <div className="mb-3 d-flex justify-content-between">
                                            <span>Amount Received:</span>
                                            <strong>{formatSummaryAmount(invoice.amountReceived || 0, getSourceCurrencySummary(), getSourceExchangeRate())}</strong>
                                        </div>
                                        <div className="mb-3 d-flex justify-content-between">
                                            <span>Balance Due:</span>
                                            <strong>{formatSummaryAmount(invoice.balanceDue || 0, getSourceCurrencySummary(), getSourceExchangeRate())}</strong>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-4">
                                    <div className="mb-4">
                                        <h6 className="mb-3">Amount in Words:</h6>
                                        <div className="text-muted">
                                            {calculateAmountInWords(invoice.total || 0)}
                                        </div>
                                    </div>
                                    <div>
                                        <h6 className="mb-3">Message:</h6>
                                        <div className="text-muted">{invoice.description || invoice.message}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right side content remains the same */}
                <div className="col-md-3 no-print">
                    {/* Right side content */}
                    <div className="card">
                        <div className="card-body">
                            {/* Action Buttons */}
                            <div className="d-grid gap-2 mb-4">
                                {/* 1. Send Invoice to Finance */}
                                {canInvoice('send_email') && (
                                    <button 
                                        className="btn btn-primary" 
                                        onClick={handleSendInvoice} 
                                        disabled={isSendingEmail || invoice?.status === 'PAID' || invoice?.status === 'FINAL' || invoice?.status === 'PARTIAL'}
                                        title={invoice?.status === 'PAID' ? 'Cannot send: Invoice is already paid' : 
                                               invoice?.status === 'FINAL' ? 'Cannot send: Invoice is finalized' :
                                               invoice?.status === 'PARTIAL' ? 'Cannot send: Invoice is partially paid' :
                                               'Send Invoice to Finance'}
                                    >
                                        {isSendingEmail ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-send me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                                    <path d="M10 14l11 -11"></path>
                                                    <path d="M21 3l-6.5 18a .55 .55 0 0 1 -1 0l-3.5 -7l-7 -3.5a.55 .55 0 0 1 0 -1l18 -6.5"></path>
                                                </svg>
                                                Send Invoice to Finance
                                            </>
                                        )}
                                    </button>
                                )}
                                
                                {/* 2. Finalize Invoice */}
                                {canInvoice('can_finalize') && (
                                    <button 
                                        className="btn btn-success" 
                                        onClick={handleFinalizeInvoice}
                                        disabled={invoice?.status === 'FINAL' || invoice?.status === 'PAID' || invoice?.status === 'PARTIAL'}
                                        title={invoice?.status === 'PAID' ? 'Cannot finalize: Invoice is already paid' : 
                                               invoice?.status === 'FINAL' ? 'Cannot finalize: Invoice is already finalized' : 
                                               invoice?.status === 'PARTIAL' ? 'Cannot finalize: Invoice is partially paid' :
                                               'Finalize Invoice'}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-check me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                            <path d="M5 12l5 5l10 -10"></path>
                                        </svg>
                                        Finalize Invoice
                                    </button>
                                )}
                                
                                {/* 3. Edit */}
                                <button 
                                    className="btn btn-outline-primary" 
                                    onClick={handleEdit} 
                                    disabled={invoice?.status === 'PAID' || invoice?.status === 'PARTIAL'}
                                    title={invoice?.status === 'PAID' || invoice?.status === 'PARTIAL' ? 
                                           'Cannot edit: Invoice is paid or partially paid' : 'Edit Invoice'}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-edit me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                        <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1"></path>
                                        <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z"></path>
                                        <path d="M16 5l3 3"></path>
                                    </svg>
                                    Edit
                                </button>
                                
                                {/* 4. Print and Download in one row */}
                                <div className="row g-2">
                                    <div className="col-6">
                                        <button className="btn btn-outline-primary w-100" onClick={handlePrint}>
                                            <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-printer me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                                <path d="M17 17h2a2 2 0 0 0 2 -2v-4a2 2 0 0 0 -2 -2h-14a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2"></path>
                                                <path d="M17 9v-4a2 2 0 0 0 -2 -2h-6a2 2 0 0 0 -2 2v4"></path>
                                                <path d="M7 13m0 2a2 2 0 0 1 2 -2h6a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-6a2 2 0 0 1 -2 -2z"></path>
                                            </svg>
                                            Print
                                        </button>
                                    </div>
                                    <div className="col-6">
                                        <button className="btn btn-outline-primary w-100" onClick={handleDownload}>
                                            <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-download me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                                <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2"></path>
                                                <path d="M7 11l5 5l5 -5"></path>
                                                <path d="M12 4l0 12"></path>
                                            </svg>
                                            Download
                                        </button>
                                    </div>
                                </div>
                                
                                {/* 5. Download Uploaded File */}
                                <button className="btn btn-outline-primary" onClick={handleDownloadUploadedPdf}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-download me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                        <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2"></path>
                                        <path d="M7 11l5 5l5 -5"></path>
                                        <path d="M12 4l0 12"></path>
                                    </svg>
                                    Download Uploaded File
                                </button>
                                
                                {/* 6. Add Payment */}
                                <button 
                                    className="btn btn-success" 
                                    onClick={handlePayment}
                                    disabled={(invoice?.status !== 'FINAL' && invoice?.status !== 'PARTIAL') || invoice?.status === 'PAID'}
                                    title={invoice?.status === 'PAID' ? 'Cannot add payment: Invoice is already paid' :
                                           invoice?.status === 'FINAL' || invoice?.status === 'PARTIAL' ? 'Add payment for this invoice' : 
                                           'Only invoices with FINAL or PARTIAL status can receive payments'}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-currency-dollar me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                        <path d="M16.7 8a3 3 0 0 0 -2.7 -2h-4a3 3 0 0 0 0 6h4a3 3 0 0 1 0 6h-4a3 3 0 0 1 -2.7 -2"></path>
                                        <path d="M12 3v3m0 12v3"></path>
                                    </svg>
                                    Add Payment
                                </button>
                            </div>

                            {/* Invoice Info */}
                            <div className="mb-4">
                                <h4 className="mb-3">Invoice Info</h4>
                                <div className="mb-2">
                                    <label className="form-label text-muted mb-1">Client Name</label>
                                    <div>
                                        {invoice.companyId ? (
                                            <a
                                                href={`/clients/${invoice.companyId}`}
                                                onClick={(e) => { e.preventDefault(); navigate(`/clients/${invoice.companyId}`); }}
                                            >
                                                {invoice.clientName}
                                            </a>
                                        ) : (
                                            invoice.clientName
                                        )}
                                    </div>
                                </div>
                                <div className="mb-2">
                                    <label className="form-label text-muted mb-1">Project Name</label>
                                    <div>
                                        {invoice.leadId ? (
                                            <a
                                                href={`/projects/detail/${invoice.leadId}`}
                                                onClick={(e) => { e.preventDefault(); navigate(`/projects/detail/${invoice.leadId}`); }}
                                            >
                                                {invoice.projectName}
                                            </a>
                                        ) : (
                                            invoice.projectName
                                        )}
                                    </div>
                                </div>
                                <div className="mb-2">
                                    <label className="form-label text-muted mb-1">Status</label>
                                    <div>
                                        <span className={getStatusBadge(invoice.status)}>
                                            {invoice.status}
                                        </span>
                                    </div>
                                </div>
                                <div className="mb-2">
                                    <label className="form-label text-muted mb-1">Last Email Sent</label>
                                    <div>{invoice.lastEmailSent ? new Date(invoice.lastEmailSent).toLocaleDateString() : 'Never'}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add ItemForm modal */}
            <ItemForm
                isOpen={isItemModalOpen}
                onClose={() => {
                    setIsItemModalOpen(false);
                    setEditingItem(null);
                }}
                onSubmit={handleItemSubmit}
                editingItem={editingItem}
                invoiceId={invoice?.id}
                currencyCode={getEffectiveCurrencyCode()}
                invoiceDate={invoice?.billDate}
            />

            {/* Existing InvoiceForm modal */}
            <InvoiceForm
                formData={{
                    // ✅ Use formData state (same structure as Invoice/index.jsx)
                    client: formData.client || invoice.companyId || invoice.client || '',
                    projectName: formData.projectName || invoice.projectId || invoice.projectName || '',
                    projectId: formData.projectId || invoice.projectId || null,
                    leadId: formData.leadId || invoice.leadId || null,
                    billDate: formData.billDate || invoice.billDate || new Date().toISOString().split('T')[0],
                    dueDate: formData.dueDate || invoice.dueDate || '',
                    description: formData.description || invoice.description || '',
                    jurnalNumber: formData.jurnalNumber || invoice.jurnalNumber || '',
                    invoiceTo: formData.invoiceTo || invoice.invoiceTo || '',
                    invoiceIssued: formData.invoiceIssued || invoice.invoiceIssued || '',
                    tax: formData.tax || invoice.taxPercentage || invoice.tax || 0,
                    applyTax: formData.applyTax !== undefined ? formData.applyTax : (invoice.applyTax || false),
                    documents: uploadedFile ? [{
                        name: uploadedFile.name,
                        size: `${(uploadedFile.size / 1024).toFixed(1)} KB`,
                        url: URL.createObjectURL(uploadedFile),
                        file: uploadedFile
                    }] : (formData.documents || [])
                }}
                formErrors={formErrors}
                handleFieldBlur={(name, value) => {
                    // ✅ Validate individual field on blur (same as Invoice/index.jsx)
                    const validationData = {
                        [name]: value,
                        isEditMode: true
                    };
                    const errors = validateInvoiceForm(validationData);

                    if (errors[name]) {
                        setFormErrors(prev => ({
                            ...prev,
                            [name]: errors[name]
                        }));
                    }
                }}
                handleInputChange={(name, value) => {
                    // ✅ Handle file upload (documents field) - same as Invoice/index.jsx
                    if (name === 'documents') {
                        if (value && value.length > 0 && value[0].file) {
                            setUploadedFile(value[0].file);
                        } else {
                            setUploadedFile(null);
                        }
                    } else {
                        // ✅ AUTO-CHECK APPLY TAX: Automatically check applyTax when tax percentage is not 0 (same as Invoice/index.jsx)
                        if (name === 'tax') {
                            const taxValue = typeof value === 'string' ? parseFloat(value) || 0 : (value || 0);
                            setFormData(prev => ({
                                ...prev,
                                tax: taxValue,
                                applyTax: taxValue > 0 // Automatically check if tax > 0, uncheck if tax = 0
                            }));
                        } else {
                            setFormData(prev => ({
                                ...prev,
                                [name]: value
                            }));
                        }
                    }

                    // Clear error when field is edited (same as Invoice/index.jsx)
                    if (formErrors[name]) {
                        setFormErrors(prev => ({
                            ...prev,
                            [name]: ''
                        }));
                    }
                }}
                handleFormSubmit={handleFormSubmit}
                isOpen={isModalOpen}
                onClose={() => {
                    // Clear uploaded file when modal closes
                    setUploadedFile(null);
                    handleModalClose();
                }}
                isEditMode={true}
                editingInvoice={invoice}
                onSuccessSubmit={() => {
                    setUploadedFile(null); // Clear file after success
                    fetchInvoiceDetail();
                }}
                isSubmitting={isSubmitting}
            />

            {/* Confirmation Modal for Send Invoice */}
            <ConfirmationModal
                isOpen={showSendConfirmation}
                onClose={() => setShowSendConfirmation(false)}
                onConfirm={confirmSendInvoice}
                title="Send Invoice"
                message={`Are you sure you want to send invoice ${invoice?.invoiceNumber || ''} to ${invoice?.clientName || 'the client'}?`}
                confirmText="Send"
                cancelText="Cancel"
                confirmVariant="primary"
                modalId="modal-send-invoice"
            />

            {/* Confirmation Modal for Delete Item */}
            <ConfirmationModal
                isOpen={showDeleteItemModal}
                onClose={() => {
                    setShowDeleteItemModal(false);
                    setItemToDelete(null);
                }}
                onConfirm={confirmDeleteItem}
                title="Delete Item"
                message={`Are you sure you want to delete "${itemToDelete?.description || 'this item'}"? This action cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                confirmVariant="danger"
                modalId="modal-delete-item"
            />

            {/* Confirmation Modal for Finalize Invoice */}
            <ConfirmationModal
                isOpen={showFinalizeModal}
                onClose={() => setShowFinalizeModal(false)}
                onConfirm={confirmFinalizeInvoice}
                title="Finalize Invoice"
                message={`Are you sure you want to finalize this invoice?`}
                confirmText="Finalize"
                cancelText="Cancel"
                confirmVariant="success"
                modalId="modal-finalize-invoice"
            />

            {/* Payment Form Modal */}
            <PaymentForm
                isOpen={isPaymentModalOpen}
                onClose={() => {
                    setIsPaymentModalOpen(false);
                    setPaymentFormData({
                        client: '',
                        invoice: '',
                        paymentMethod: '',
                        paymentDate: new Date().toISOString().split('T')[0],
                        amount: '',
                        note: '',
                        file: null
                    });
                    setPaymentFormErrors({});
                }}
                formData={paymentFormData}
                formErrors={paymentFormErrors}
                handleInputChange={handlePaymentInputChange}
                handleFormSubmit={handlePaymentFormSubmit}
                isEditMode={false}
                editingPayment={null}
                disableClientSelect={true}
                disableInvoiceSelect={true}
                currencyCode={getEffectiveCurrencyCode()}
            />
        </div>
    );
};

export default InvoiceView; 
